import React, { useState } from "react";
import { Download } from "lucide-react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatRestrictedSqlValue, normalizeRestrictedSqlMeasures } from "./customReportMeasures.js";

const COLORS = ["#4f46e5", "#0891b2", "#d97706", "#16a34a", "#9333ea"];
const GRAIN_LABELS = {
  hour: "Hourly",
  day: "Daily",
  week: "Weekly",
  month: "Monthly",
};

const isMeasureColumn = (column) =>
  column?.role === "measure" || column?.kind === "measure" || column?.type === "measure";

export const formatEpochBucketLabel = (epochSeconds, grain, full = false) => {
  const parsed = new Date(Number(epochSeconds) * 1000);
  if (Number.isNaN(parsed.getTime())) {
    return epochSeconds === null || epochSeconds === undefined ? "-" : String(epochSeconds);
  }
  const base = { timeZone: "Asia/Kolkata" };
  const options = full
    ? { ...base, day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }
    : grain === "hour"
      ? { ...base, day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }
      : grain === "month"
        ? { ...base, month: "short", year: "numeric" }
        : { ...base, day: "2-digit", month: "short", year: "2-digit" };
  return new Intl.DateTimeFormat("en-IN", options).format(parsed);
};

export const formatEpochBucketRange = (bucketStart, bucketEnd, grain) =>
  `${formatEpochBucketLabel(bucketStart, grain, true)} – ${formatEpochBucketLabel(
    bucketEnd,
    grain,
    true
  )}`;

const formatTimeFieldLabel = (field) =>
  String(field || "Time")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());

export const formatGrainLabel = (grain) =>
  GRAIN_LABELS[String(grain || "day").toLowerCase()] || String(grain || "day");

const isIntegerFormat = (format) =>
  ["integer", "number"].includes(String(format || "").toLowerCase());

const formatAxisValue = (value, format) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return value;
  return new Intl.NumberFormat("en-IN", {
    notation: Math.abs(numeric) >= 1000 ? "compact" : "standard",
    maximumFractionDigits: isIntegerFormat(format) ? 0 : 2,
  }).format(numeric);
};

const safeSvgId = (value) => String(value || "time-series").replace(/[^a-zA-Z0-9_-]/g, "-");

export const sortTimeSeriesRows = (
  rows,
  bucketStartKey = "bucket_start",
  direction = "asc"
) =>
  [...(Array.isArray(rows) ? rows : [])].sort((left, right) => {
    const comparison =
      Number(left?.[bucketStartKey]) - Number(right?.[bucketStartKey]);
    return direction === "desc" ? -comparison : comparison;
  });

export const sortTimeSeriesRowsNewestFirst = (rows, bucketStartKey = "bucket_start") =>
  sortTimeSeriesRows(rows, bucketStartKey, "desc");

const TimeSeriesTable = ({
  rows,
  measureColumns,
  bucketStartKey,
  bucketEndKey,
  grain,
  getMeasureLabel,
  getMeasureFormat,
  totalValues,
  onPointClick,
  canDrilldown,
}) => (
  <div className="min-h-0 flex-1 overflow-auto" data-testid="time-series-table">
    <table className="min-w-max border-separate border-spacing-0">
      <thead>
        <tr className="bg-gray-50">
          <th scope="col" className="sticky left-0 top-0 z-20 min-w-[12rem] border-b border-r border-gray-200 bg-gray-50 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
            Metric
          </th>
          <th
            scope="col"
            className="sticky top-0 z-10 min-w-[8.5rem] border-b border-r border-gray-200 bg-gray-100 px-4 py-3 text-right text-xs font-semibold text-gray-600"
          >
            Total
          </th>
          {rows.map((row) => (
            <th
              key={`time-${row[bucketStartKey]}`}
              scope="col"
              title={formatEpochBucketRange(row[bucketStartKey], row[bucketEndKey], grain)}
              className="sticky top-0 z-10 min-w-[8.5rem] border-b border-r border-gray-200 bg-gray-50 px-4 py-3 text-right text-xs font-semibold text-gray-600"
            >
              {formatEpochBucketLabel(row[bucketStartKey], grain)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {measureColumns.map((column) => (
          <tr key={`metric-${column.key}`} className="hover:bg-gray-50/70">
            <th scope="row" className="sticky left-0 z-10 border-b border-r border-gray-100 bg-white px-4 py-3 text-left text-sm font-medium text-gray-900">
              {getMeasureLabel(column)}
            </th>
            <td className="border-b border-r border-gray-100 bg-gray-50 px-4 py-3 text-right text-sm font-semibold tabular-nums text-gray-900">
              {onPointClick &&
              canDrilldown &&
              column?.drilldownMode === "exact_count" &&
              Number(totalValues?.[column.key]) > 0 ? (
                <button
                  type="button"
                  title="View customers for the complete selected time range"
                  onClick={() =>
                    onPointClick(
                      {
                        __timeSeriesScope: "total",
                        [column.key]: totalValues?.[column.key],
                      },
                      column
                    )
                  }
                  className="font-semibold text-indigo-600 underline decoration-indigo-200 underline-offset-2 hover:text-indigo-700"
                >
                  {formatRestrictedSqlValue(
                    totalValues?.[column.key],
                    getMeasureFormat(column)
                  )}
                </button>
              ) : (
                formatRestrictedSqlValue(
                  totalValues?.[column.key],
                  getMeasureFormat(column)
                )
              )}
            </td>
            {rows.map((row) => {
              const formattedValue = formatRestrictedSqlValue(
                row[column.key],
                getMeasureFormat(column)
              );
              const isDrillable =
                canDrilldown &&
                column?.drilldownMode === "exact_count" &&
                Number(row?.[column.key]) > 0;
              return (
                <td
                  key={`${column.key}-${row[bucketStartKey]}`}
                  className="border-b border-r border-gray-100 px-4 py-3 text-right text-sm tabular-nums text-gray-900"
                >
                  {onPointClick && isDrillable ? (
                    <button
                      type="button"
                      title={`View customers for ${formatEpochBucketRange(
                        row[bucketStartKey],
                        row[bucketEndKey],
                        grain
                      )}`}
                      onClick={() => onPointClick(row, column)}
                      className="font-medium text-indigo-600 underline decoration-indigo-200 underline-offset-2 hover:text-indigo-700"
                    >
                      {formattedValue}
                    </button>
                  ) : (
                    formattedValue
                  )}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const TimeSeriesVisualization = ({ visualization, onDownload, onPointClick }) => {
  const data = visualization?.data || {};
  const rows = Array.isArray(data.rows) ? data.rows : [];
  const columns = Array.isArray(data.columns) ? data.columns : [];
  const responseConfig = data.timeSeries || data.time_series || {};
  const savedConfig = visualization?.timeSeries || visualization?.config?.timeSeries || {};
  const config = { ...savedConfig, ...responseConfig };
  const timeKey = config.timeKey || "time_bucket";
  const displayTimeKey = "time_bucket";
  const bucketStartKey = config.bucketStartKey || "bucket_start";
  const bucketEndKey = config.bucketEndKey || "bucket_end";
  const configuredMeasures = normalizeRestrictedSqlMeasures(visualization?.measures);
  const measureById = new Map(configuredMeasures.map((measure) => [String(measure.id), measure]));
  const measureColumns = columns.filter(isMeasureColumn).length
    ? columns.filter(isMeasureColumn)
    : Object.keys(rows[0] || {})
        .filter((key) => ![timeKey, displayTimeKey, bucketStartKey, bucketEndKey].includes(key))
        .map((key) => ({ key, label: key, role: "measure" }));
  const getMeasureLabel = (column) =>
    column?.display_label ||
    column?.displayLabel ||
    column?.label ||
    measureById.get(String(column?.measure_id ?? column?.measureId ?? column?.key))?.label ||
    column?.key;
  const getMeasureFormat = (column) =>
    column?.format ||
    measureById.get(String(column?.measure_id ?? column?.measureId ?? column?.key))?.format;
  const hasInvalidBuckets = rows.some(
    (row) =>
      !Number.isInteger(Number(row?.[bucketStartKey])) ||
      !Number.isInteger(Number(row?.[bucketEndKey])) ||
      Number(row?.[bucketEndKey]) < Number(row?.[bucketStartKey])
  );
  const bucketIdentities = rows.map((row) => String(row?.[bucketStartKey]));
  const hasDuplicateBuckets = new Set(bucketIdentities).size !== bucketIdentities.length;
  const visualizationId = String(visualization?.id || "time-series");
  const savedDisplayMode = config.displayMode === "table" ? "table" : "trendline";
  const [viewState, setViewState] = useState({
    visualizationId,
    savedDisplayMode,
    displayMode: savedDisplayMode,
    dateOrder: "desc",
  });
  const hasCurrentViewState =
    viewState.visualizationId === visualizationId &&
    viewState.savedDisplayMode === savedDisplayMode;
  const displayMode = hasCurrentViewState
    ? viewState.displayMode
    : savedDisplayMode;
  const dateOrder = hasCurrentViewState ? viewState.dateOrder : "desc";
  const updateViewState = (patch) =>
    setViewState((current) => ({
      ...(current.visualizationId === visualizationId &&
      current.savedDisplayMode === savedDisplayMode
        ? current
        : {
            visualizationId,
            savedDisplayMode,
            displayMode: savedDisplayMode,
            dateOrder: "desc",
          }),
      ...patch,
    }));
  const orderedRows = sortTimeSeriesRows(rows, bucketStartKey, dateOrder);
  const chartRows = orderedRows;
  const tableRows = orderedRows;
  const totalValues =
    data?.totals?.grand &&
    typeof data.totals.grand === "object" &&
    !Array.isArray(data.totals.grand)
      ? data.totals.grand
      : {};
  const [visibilityState, setVisibilityState] = useState({
    visualizationId,
    hiddenMeasureKeys: [],
  });
  const hiddenMeasureKeys = new Set(
    visibilityState.visualizationId === visualizationId
      ? visibilityState.hiddenMeasureKeys
      : []
  );
  const visibleMeasureColumns = measureColumns.filter(
    (column) => !hiddenMeasureKeys.has(String(column.key))
  );
  const singleMeasureColumn =
    visibleMeasureColumns.length === 1 ? visibleMeasureColumns[0] : null;
  const singleMeasureKey = singleMeasureColumn?.key;
  const singleMeasureIndex = Math.max(
    0,
    measureColumns.findIndex((column) => String(column.key) === String(singleMeasureKey))
  );
  const primaryColor = COLORS[singleMeasureIndex % COLORS.length];
  const sharedAxisUsesDecimals = visibleMeasureColumns.some(
    (column) => !isIntegerFormat(getMeasureFormat(column))
  );
  const sharedAxisFormat = sharedAxisUsesDecimals
    ? "decimal"
    : getMeasureFormat(visibleMeasureColumns[0]);
  const gradientId = `time-series-gradient-${safeSvgId(visualization?.id)}-${safeSvgId(
    singleMeasureKey
  )}`;
  const canDrilldown = Boolean(visualization?.drilldown?.context);
  const hasExactCountMeasure =
    canDrilldown &&
    measureColumns.some((column) => column?.drilldownMode === "exact_count");

  const toggleMeasure = (measureKey) => {
    const normalizedKey = String(measureKey);
    const isVisible = !hiddenMeasureKeys.has(normalizedKey);
    if (isVisible && visibleMeasureColumns.length === 1) return;
    setVisibilityState((current) => {
      const currentHidden = new Set(
        current.visualizationId === visualizationId ? current.hiddenMeasureKeys : []
      );
      if (currentHidden.has(normalizedKey)) currentHidden.delete(normalizedKey);
      else currentHidden.add(normalizedKey);
      return {
        visualizationId,
        hiddenMeasureKeys: [...currentHidden],
      };
    });
  };

  if (responseConfig.truncated) {
    return (
      <div className="flex h-full min-h-0 items-center justify-center px-6 text-center text-sm text-red-600">
        This time series exceeds the {responseConfig.pointLimit || "configured"} point limit. Narrow the time range or use a coarser grain.
      </div>
    );
  }

  if (!rows.length || !measureColumns.length) {
    return (
      <div className="flex h-full min-h-0 items-center justify-center text-sm text-gray-500">
        No time-series data available
      </div>
    );
  }

  if (hasInvalidBuckets || hasDuplicateBuckets) {
    return (
      <div className="flex h-full min-h-0 items-center justify-center px-6 text-center text-sm text-red-600">
        The time-series response contains invalid or duplicate bucket boundaries.
      </div>
    );
  }

  const header = (
    <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-100 px-4 py-3">
      <div className="min-w-0">
        <div className="truncate text-lg font-semibold text-gray-900">
          {visualization.title || `Visualization ${visualization.id}`}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
          <span>{formatTimeFieldLabel(config.field)}</span>
          <span aria-hidden="true">·</span>
          <span>{formatGrainLabel(config.grain)}</span>
          <span aria-hidden="true">·</span>
          <span>{config.timezone || "Asia/Kolkata"}</span>
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
        <div
          className="inline-flex overflow-hidden rounded-md border border-gray-200 bg-white"
          role="group"
          aria-label="Time-series display"
        >
          {[
            { value: "trendline", label: "Trendline" },
            { value: "table", label: "Table" },
          ].map((option) => (
            <button
              key={option.value}
              type="button"
              aria-pressed={displayMode === option.value}
              onClick={() => updateViewState({ displayMode: option.value })}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                displayMode === option.value
                  ? "bg-indigo-50 text-indigo-700"
                  : "text-gray-500 hover:bg-gray-50 hover:text-gray-700"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
        <div
          className="inline-flex overflow-hidden rounded-md border border-gray-200 bg-white"
          role="group"
          aria-label="Time-series date order"
        >
          {[
            { value: "asc", label: "Oldest first" },
            { value: "desc", label: "Newest first" },
          ].map((option) => (
            <button
              key={option.value}
              type="button"
              aria-pressed={dateOrder === option.value}
              onClick={() => updateViewState({ dateOrder: option.value })}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                dateOrder === option.value
                  ? "bg-indigo-50 text-indigo-700"
                  : "text-gray-500 hover:bg-gray-50 hover:text-gray-700"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          aria-label={`Download ${visualization.title || "time series"}`}
          onClick={onDownload}
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 transition-colors hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700"
        >
          <Download size={17} />
        </button>
      </div>
    </div>
  );

  if (displayMode === "table") {
    return (
      <div className="flex h-full min-h-0 flex-col bg-white">
        {header}
        <TimeSeriesTable
          rows={tableRows}
          measureColumns={measureColumns}
          bucketStartKey={bucketStartKey}
          bucketEndKey={bucketEndKey}
          grain={config.grain}
          getMeasureLabel={getMeasureLabel}
          getMeasureFormat={getMeasureFormat}
          totalValues={totalValues}
          onPointClick={onPointClick}
          canDrilldown={canDrilldown}
        />
        <div className="shrink-0 border-t border-gray-100 px-4 py-2 text-center text-[11px] text-gray-400">
          Metrics are rows; {dateOrder === "desc" ? "newest" : "oldest"} time buckets appear first.
          {hasExactCountMeasure
            ? " Select an exact count value to view its customers."
            : ""}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-white">
      {header}

      {measureColumns.length > 1 ? (
        <div
          className="flex shrink-0 items-center gap-2 overflow-x-auto border-b border-gray-100 bg-gray-50/60 px-4 py-2"
          aria-label="Time-series measures"
          role="group"
        >
          {measureColumns.map((column, index) => {
            const isVisible = !hiddenMeasureKeys.has(String(column.key));
            const isLastVisible = isVisible && visibleMeasureColumns.length === 1;
            const color = COLORS[index % COLORS.length];
            return (
              <button
                key={column.key}
                type="button"
                aria-pressed={isVisible}
                aria-label={`${isVisible ? "Hide" : "Show"} ${getMeasureLabel(column)}`}
                disabled={isLastVisible}
                onClick={() => toggleMeasure(column.key)}
                className={`inline-flex shrink-0 items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                  isVisible
                    ? "border-indigo-200 bg-white text-gray-900 shadow-sm"
                    : "border-transparent text-gray-400 opacity-60 hover:border-gray-200 hover:bg-white hover:text-gray-600"
                } ${isLastVisible ? "cursor-not-allowed" : "cursor-pointer"}`}
              >
                <span
                  aria-hidden="true"
                  className={`h-2 w-2 rounded-full ${isVisible ? "" : "ring-1 ring-gray-300"}`}
                  style={{ backgroundColor: isVisible ? color : "transparent" }}
                />
                {getMeasureLabel(column)}
              </button>
            );
          })}
        </div>
      ) : null}

      <div className="min-h-0 flex-1 px-2 pb-1 pt-3" data-testid="time-series-chart">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartRows} margin={{ top: 8, right: 20, left: 4, bottom: 8 }}>
            {singleMeasureColumn ? (
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={primaryColor} stopOpacity={0.2} />
                  <stop offset="95%" stopColor={primaryColor} stopOpacity={0.015} />
                </linearGradient>
              </defs>
            ) : null}
            <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#eef2f7" />
            <XAxis
              dataKey={bucketStartKey}
              minTickGap={28}
              axisLine={false}
              tickLine={false}
              tickMargin={10}
              tick={{ fontSize: 11, fill: "#6b7280" }}
              tickFormatter={(value) => formatEpochBucketLabel(value, config.grain)}
            />
            <YAxis
              yAxisId="left"
              orientation="left"
              allowDecimals={sharedAxisUsesDecimals}
              axisLine={false}
              tickLine={false}
              tickMargin={8}
              tick={{ fontSize: 11, fill: "#6b7280" }}
              tickFormatter={(value) => formatAxisValue(value, sharedAxisFormat)}
              width={58}
            />
            <Tooltip
              cursor={{ stroke: "#9ca3af", strokeDasharray: "4 4", strokeWidth: 1 }}
              wrapperStyle={{ pointerEvents: "auto" }}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const sourceRow = payload[0]?.payload || {};
                return (
                  <div className="min-w-[13rem] rounded-lg border border-gray-100 bg-white p-3 text-xs shadow-xl">
                    <div className="mb-2 text-[11px] font-medium text-gray-500">
                      {formatEpochBucketRange(
                        sourceRow[bucketStartKey],
                        sourceRow[bucketEndKey],
                        config.grain
                      )}
                    </div>
                    <div className="space-y-1.5">
                      {visibleMeasureColumns.map((column) => {
                        const index = measureColumns.findIndex(
                          (candidate) => String(candidate.key) === String(column.key)
                        );
                        const isDrillable =
                          canDrilldown &&
                          column?.drilldownMode === "exact_count" &&
                          Number(sourceRow?.[column.key]) > 0;
                        const formattedValue = formatRestrictedSqlValue(
                          sourceRow[column.key],
                          getMeasureFormat(column)
                        );
                        return (
                          <div
                            key={column.key}
                            className="flex items-center justify-between gap-5"
                          >
                            <span className="inline-flex items-center gap-2 text-gray-600">
                              <span
                                aria-hidden="true"
                                className="h-2 w-2 rounded-full"
                                style={{ backgroundColor: COLORS[index % COLORS.length] }}
                              />
                              {getMeasureLabel(column)}
                            </span>
                            {isDrillable ? (
                              <button
                                type="button"
                                className="font-semibold tabular-nums text-indigo-600 underline decoration-indigo-200 underline-offset-2 hover:text-indigo-700"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  onPointClick?.(sourceRow, column);
                                }}
                              >
                                {formattedValue}
                              </button>
                            ) : (
                              <span className="font-semibold tabular-nums text-gray-900">
                                {formattedValue}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {canDrilldown && visibleMeasureColumns.some(
                      (column) =>
                        column?.drilldownMode === "exact_count" &&
                        Number(sourceRow?.[column.key]) > 0
                    ) ? (
                      <div className="mt-2 border-t border-gray-100 pt-2 text-[11px] text-gray-400">
                        Click an underlined count to view its customers
                      </div>
                    ) : null}
                  </div>
                );
              }}
            />
            {singleMeasureColumn ? (
              <Area
                type="monotoneX"
                dataKey={singleMeasureColumn.key}
                yAxisId="left"
                fill={`url(#${gradientId})`}
                stroke="none"
                connectNulls={false}
                dot={false}
                activeDot={false}
                isAnimationActive={false}
              />
            ) : null}
            {visibleMeasureColumns.map((column, index) => (
              <Line
                key={column.key}
                type="monotoneX"
                dataKey={column.key}
                yAxisId="left"
                name={getMeasureLabel(column)}
                stroke={COLORS[measureColumns.indexOf(column) % COLORS.length]}
                strokeWidth={index === 0 ? 2.75 : 2.25}
                strokeLinecap="round"
                strokeLinejoin="round"
                connectNulls={false}
                dot={false}
                activeDot={
                  canDrilldown && column?.drilldownMode === "exact_count"
                    ? (dotProps) => {
                        const sourceRow = dotProps?.payload || {};
                        const isDrillable = Number(sourceRow?.[column.key]) > 0;
                        return (
                          <circle
                            cx={dotProps.cx}
                            cy={dotProps.cy}
                            r={5}
                            fill={COLORS[measureColumns.indexOf(column) % COLORS.length]}
                            stroke="#ffffff"
                            strokeWidth={2}
                            role={isDrillable ? "button" : undefined}
                            aria-label={
                              isDrillable
                                ? `View customers for ${getMeasureLabel(column)} on ${formatEpochBucketRange(
                                    sourceRow[bucketStartKey],
                                    sourceRow[bucketEndKey],
                                    config.grain
                                  )}`
                                : undefined
                            }
                            tabIndex={isDrillable ? 0 : undefined}
                            style={{ cursor: isDrillable ? "pointer" : "default" }}
                            onClick={
                              isDrillable
                                ? (event) => {
                                    event.stopPropagation();
                                    onPointClick?.(sourceRow, column);
                                  }
                                : undefined
                            }
                            onKeyDown={
                              isDrillable
                                ? (event) => {
                                    if (event.key === "Enter" || event.key === " ") {
                                      event.preventDefault();
                                      onPointClick?.(sourceRow, column);
                                    }
                                  }
                                : undefined
                            }
                          />
                        );
                      }
                    : { r: 4, stroke: "#ffffff", strokeWidth: 2 }
                }
                isAnimationActive={false}
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {hasExactCountMeasure ? (
        <div className="shrink-0 px-4 pb-3 pt-1 text-center text-[11px] text-gray-400">
          Click an exact count point to view its customers in that {config.grain || "day"} bucket.
        </div>
      ) : null}
    </div>
  );
};

export default TimeSeriesVisualization;
